/**
 * Mehr-Standort Phase 4 — Org-weite Aufgaben (Hauptsitz).
 *
 * Der Hauptsitz sieht alle delegierten Aufgaben über sämtliche Filialen hinweg
 * (Filter: offen / überfällig / erledigt-unbestätigt / alle) und kann eine neue
 * Aufgabe an einen Mitarbeiter einer beliebigen Filiale zuweisen.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ListTodo, Loader2, Plus, AlertTriangle, Bell, ChevronDown, Clock, UserPlus, Check, X, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { usePersistentToggle } from "../../lib/usePersistentToggle";

const TASK_AREAS = ["Verkauf", "Ankauf", "Werkstatt", "Disposition", "Warenwirtschaft", "Buchhaltung"];
const STATUS_FILTERS = [
    { key: "open", label: "Offen" },
    { key: "overdue", label: "Überfällig" },
    { key: "done_unack", label: "Erledigt" },
    { key: "all", label: "Alle" },
];

const STATUS_BADGE = {
    open: { t: "offen", c: "bg-violet-100 text-violet-800" },
    done: { t: "erledigt", c: "bg-emerald-100 text-emerald-800" },
};

export default function OrgTasksPanel({ scope = "all" }) {
    const navigate = useNavigate();
    const [status, setStatus] = useState("open");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [empGroups, setEmpGroups] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ branch_id: "", assignee_id: "", area: "Verkauf", title: "", body: "" });
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = usePersistentToggle("org-tasks-open", false);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [snoozeFor, setSnoozeFor] = useState(null);
    const [snoozeDate, setSnoozeDate] = useState("");
    const [reassignTask, setReassignTask] = useState(null);
    const [reForm, setReForm] = useState({ branch_id: "", assignee_id: "", note: "" });
    const [reBusy, setReBusy] = useState(false);
    const [site, setSite] = useState("all");
    const [area, setArea] = useState("all");
    // Aug-2026 (User-Wunsch): Briefing-Chip springt direkt hierher (?focus=org-tasks)
    const [searchParams, setSearchParams] = useSearchParams();
    const panelRef = useRef(null);
    useEffect(() => {
        if (searchParams.get("focus") !== "org-tasks") return;
        setOpen(true);
        if (searchParams.get("tstatus") === "overdue") setStatus("overdue");
        setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
        const n = new URLSearchParams(searchParams);
        n.delete("focus"); n.delete("tstatus");
        setSearchParams(n, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    // Aug-2026 (User-Wunsch): Alt-/Testaufgaben bereinigen
    const [cleanupOpen, setCleanupOpen] = useState(false);
    const [cleanupDays, setCleanupDays] = useState("30");
    const [cleanupBusy, setCleanupBusy] = useState(false);
    const runCleanup = async () => {
        setCleanupBusy(true);
        try {
            const r = await api.post("/org/tasks/cleanup", {
                branch_id: effScope !== "all" ? effScope : null,
                area: area !== "all" ? area : null,
                duplicates_only: cleanupDays === "duplicates",
                older_than_days: (cleanupDays === "all" || cleanupDays === "duplicates") ? null : Number(cleanupDays),
            });
            toast.success(`${r.data?.deleted ?? 0} Aufgabe(n) gelöscht`);
            setCleanupOpen(false);
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bereinigen fehlgeschlagen");
        } finally { setCleanupBusy(false); }
    };

    const effScope = site !== "all" ? site : scope;

    const reqRef = useRef(0);
    const load = useCallback((sc, st, ar) => {
        const rid = ++reqRef.current;
        setLoading(true);
        api.get("/org/tasks", { params: { scope: sc, status: st, area: ar && ar !== "all" ? ar : undefined } })
            .then((r) => { if (rid === reqRef.current) setData(r.data); })
            .catch(() => { if (rid === reqRef.current) setData(null); })
            .finally(() => { if (rid === reqRef.current) setLoading(false); });
    }, []);

    useEffect(() => { setPage(0); load(effScope, status, area); }, [effScope, status, area, load]);

    useEffect(() => {
        api.get("/org/assignable-employees")
            .then((r) => setEmpGroups(r.data?.locations || []))
            .catch(() => setEmpGroups([]));
    }, []);

    const branchEmployees = empGroups.find((g) => g.dealer_id === form.branch_id)?.employees || [];

    const submit = async () => {
        if (!form.branch_id || !form.assignee_id || !form.title.trim()) {
            toast.error("Bitte Standort, Mitarbeiter und Titel angeben");
            return;
        }
        setSaving(true);
        try {
            await api.post("/org/tasks", {
                branch_id: form.branch_id, assignee_id: form.assignee_id,
                area: form.area, title: form.title.trim(), body: form.body.trim() || null,
            });
            toast.success("Aufgabe zugewiesen");
            setDialogOpen(false);
            setForm({ branch_id: "", assignee_id: "", area: "Verkauf", title: "", body: "" });
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisen fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const items = data?.items || [];
    const summary = data?.summary || {};
    const autoRemindedToday = data?.auto_reminded_today || 0;

    const remind = async (taskId) => {
        try {
            const r = await api.post(`/org/tasks/${taskId}/remind`);
            toast.success(r.data?.pushed ? "Erinnerung gesendet (Push + E-Mail)" : "Erinnerung gesendet (E-Mail)");
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erinnern fehlgeschlagen");
        }
    };

    const remindOverdue = async () => {
        try {
            const r = await api.post("/org/tasks/remind-overdue", {
                branch_id: effScope !== "all" ? effScope : null,
                area: area !== "all" ? area : null,
            });
            toast.success(`${r.data?.reminded ?? 0} überfällige Aufgabe(n) erinnert`);
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sammel-Erinnerung fehlgeschlagen");
        }
    };

    const snooze = async (taskId, payload) => {
        try {
            const r = await api.post(`/org/tasks/${taskId}/snooze`, payload);
            const until = new Date(r.data.snooze_until);
            toast.success(`Aufgabe schlummert bis ${until.toLocaleDateString("de-DE")}`);
            setSnoozeFor(null); setSnoozeDate("");
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Schlummern fehlgeschlagen");
        }
    };

    const markDone = async (taskId) => {
        try {
            await api.post(`/org/tasks/${taskId}/done`);
            toast.success("Aufgabe als erledigt markiert");
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erledigen fehlgeschlagen");
        }
    };

    const openReassign = (t) => {
        setReForm({ branch_id: t.dealer_id || "", assignee_id: "", note: "" });
        setReassignTask(t);
    };

    const submitReassign = async () => {
        if (!reForm.branch_id || !reForm.assignee_id) {
            toast.error("Bitte Standort und Mitarbeiter wählen");
            return;
        }
        setReBusy(true);
        try {
            const r = await api.post(`/org/tasks/${reassignTask.id}/reassign`, reForm);
            toast.success(`Neu zugewiesen an ${r.data.assignee_name}`);
            setReassignTask(null);
            load(effScope, status, area);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Neu zuweisen fehlgeschlagen");
        } finally {
            setReBusy(false);
        }
    };

    const reBranchEmployees = empGroups.find((g) => g.dealer_id === reForm.branch_id)?.employees || [];


    return (
        <div className="mb-2" data-testid="org-tasks-panel" ref={panelRef}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <button type="button" onClick={() => setOpen((v) => !v)}
                    className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                    data-testid="org-tasks-toggle" aria-expanded={open}>
                    <ListTodo className="w-3.5 h-3.5" /> Org-weite Aufgaben
                    <span className="ml-1 text-gray-400">({items.length})</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                <div className="flex items-center gap-1" data-testid="org-tasks-status-filter">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setStatus(f.key)}
                            className={`h-6 px-2 rounded-sm text-[10px] font-bold uppercase tracking-wider border transition-colors ${status === f.key ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                            data-testid={`org-tasks-filter-${f.key}`}
                        >
                            {f.label}
                            {f.key === "overdue" && summary.overdue > 0 && <span className="ml-1 text-red-600">{summary.overdue}</span>}
                        </button>
                    ))}
                </div>
                {empGroups.length > 1 && (
                    <select
                        value={site}
                        onChange={(e) => setSite(e.target.value)}
                        className="h-6 px-1.5 rounded-sm text-[10px] font-bold border border-gray-200 bg-white text-gray-600"
                        title="Aufgaben eines einzelnen Standorts anzeigen"
                        data-testid="org-tasks-site-filter"
                    >
                        <option value="all">Alle Standorte</option>
                        {empGroups.map((g) => (
                            <option key={g.dealer_id} value={g.dealer_id}>
                                {g.site_code} · {g.company_name}
                            </option>
                        ))}
                    </select>
                )}
                <select
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    className="h-6 px-1.5 rounded-sm text-[10px] font-bold border border-gray-200 bg-white text-gray-600"
                    title="Aufgaben nach Bereich filtern"
                    data-testid="org-tasks-area-filter"
                >
                    <option value="all">Alle Bereiche</option>
                    {TASK_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <button
                    type="button"
                    onClick={() => setCleanupOpen(true)}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 border border-gray-300 hover:border-red-400 hover:text-red-600 rounded-sm px-2.5 py-1"
                    title="Alte/Test-Aufgaben im aktuellen Filter löschen"
                    data-testid="org-tasks-cleanup-btn"
                >
                    <Trash2 className="w-3.5 h-3.5" /> Bereinigen
                </button>
                <button
                    type="button"
                    onClick={() => setDialogOpen(true)}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-[#FFCC00] !text-[#111827] hover:brightness-95 rounded-sm px-2.5 py-1"
                    data-testid="org-tasks-delegate-btn"
                >
                    <Plus className="w-3.5 h-3.5" /> Aufgabe zuweisen
                </button>
            </div>

            {cleanupOpen && (
                <Dialog open onOpenChange={() => setCleanupOpen(false)}>
                    <DialogContent className="max-w-md" data-testid="org-tasks-cleanup-dialog">
                        <DialogHeader><DialogTitle>Aufgaben bereinigen</DialogTitle></DialogHeader>
                        <p className="text-sm text-gray-600">
                            Löscht <b>offene</b> Aufgaben im aktuellen Filter
                            ({site === "all" ? "alle Standorte" : "gewählter Standort"} · {area === "all" ? "alle Bereiche" : area}) — 
                            z. B. alte Test- oder Automatik-Aufgaben. Das kann nicht rückgängig gemacht werden.
                        </p>
                        <select value={cleanupDays} onChange={(e) => setCleanupDays(e.target.value)}
                            className="h-9 w-full border border-gray-300 rounded-sm px-2 text-sm bg-white" data-testid="org-tasks-cleanup-days">
                            <option value="duplicates">Nur Duplikate (gleiche Anfrage mehrfach)</option>
                            <option value="30">Nur älter als 30 Tage</option>
                            <option value="14">Nur älter als 14 Tage</option>
                            <option value="7">Nur älter als 7 Tage</option>
                            <option value="all">ALLE offenen im Filter</option>
                        </select>
                        <DialogFooter>
                            <button type="button" onClick={() => setCleanupOpen(false)} className="h-9 px-3 rounded-sm border border-gray-300 text-sm font-bold" data-testid="org-tasks-cleanup-cancel">Abbrechen</button>
                            <button type="button" onClick={runCleanup} disabled={cleanupBusy}
                                className="h-9 px-3 rounded-sm bg-red-600 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="org-tasks-cleanup-confirm">
                                {cleanupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Jetzt löschen
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {open && (<>
            {autoRemindedToday > 0 && (
                <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5" data-testid="org-tasks-auto-reminded">
                    <Bell className="w-3.5 h-3.5" /> {autoRemindedToday} Aufgabe(n) heute automatisch erinnert (&gt; 3 Tage überfällig)
                </div>
            )}

            {loading && !data ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center" data-testid="org-tasks-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lade Aufgaben…
                </div>
            ) : items.length === 0 ? (
                <p className="text-xs text-gray-400 py-3" data-testid="org-tasks-empty">Keine Aufgaben in dieser Ansicht.</p>
            ) : (
                <>
                <ul className="space-y-1.5" data-testid="org-tasks-list">
                    {items.filter((t) => t.overdue).length > 0 && (
                        <li>
                            <button
                                type="button"
                                onClick={remindOverdue}
                                className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white bg-red-600 hover:bg-red-700 rounded-sm px-3 py-1.5 mb-0.5"
                                data-testid="org-tasks-remind-overdue"
                            >
                                <Bell className="w-3.5 h-3.5" /> Alle überfälligen erinnern ({items.filter((t) => t.overdue).length})
                            </button>
                        </li>
                    )}
                    {items.slice(page * pageSize, page * pageSize + pageSize).map((t) => {
                        const badge = STATUS_BADGE[t.status] || { t: t.status, c: "bg-gray-100 text-gray-600" };
                        return (
                            <li key={t.id} onClick={() => t.cta_url && navigate(t.cta_url)} title={t.cta_url ? "Zum Vorgang öffnen" : undefined}
                                className={`border rounded-sm px-2.5 py-2 transition-colors ${t.cta_url ? "cursor-pointer" : ""} ${t.overdue ? "border-red-300 bg-red-50 hover:bg-red-100" : "border-gray-200 bg-white hover:bg-gray-50"}`} data-testid={`org-task-${t.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]" title={t.branch_company_name}>{t.branch_site_code}</span>
                                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827]">{t.area}</span>
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${badge.c}`}>{badge.t}</span>
                                    {t.overdue && (
                                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white">
                                            <AlertTriangle className="w-3 h-3" /> Überfällig
                                        </span>
                                    )}
                                    {t.snoozed && (
                                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-violet-600 text-white" data-testid={`org-task-snoozed-${t.id}`}>
                                            <Clock className="w-3 h-3" /> schlummert bis {new Date(t.snooze_until).toLocaleDateString("de-DE")}
                                        </span>
                                    )}
                                    <span className="font-bold text-sm text-gray-900">{t.title}</span>
                                    {t.grouped_count > 1 && (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800 border border-blue-200"
                                            title={`${t.grouped_count} Erinnerungen zu diesem Lead zusammengefasst — eine Bearbeitung gilt für alle`}
                                            data-testid={`org-task-grouped-${t.id}`}>
                                            <Layers className="w-3 h-3" /> {t.grouped_count}×
                                        </span>
                                    )}
                                    {t.reminder_count > 0 && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-700" title={`${t.reminder_count}× erinnert`} data-testid={`org-task-reminded-${t.id}`}>
                                            <Bell className="w-3 h-3" />{t.reminder_count}
                                        </span>
                                    )}
                                    <span className="ml-auto text-[10px] text-gray-500">→ {t.assignee_name}</span>
                                </div>
                                {t.body && <p className="text-xs text-gray-600 mt-0.5">{t.body}</p>}
                                {t.reassign_note && <p className="text-xs text-violet-700 mt-0.5" data-testid={`org-task-note-${t.id}`}>Hinweis: {t.reassign_note}</p>}
                                {t.status === "open" && (
                                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); remind(t.id); }}
                                            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1 border ${t.overdue ? "text-red-700 border-red-300 hover:bg-red-100 bg-white" : "text-gray-600 border-gray-300 hover:bg-gray-50 bg-white"}`}
                                            data-testid={`org-task-remind-${t.id}`}
                                        >
                                            <Bell className="w-3 h-3" /> An Filiale erinnern
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setSnoozeFor(snoozeFor === t.id ? null : t.id); setSnoozeDate(""); }}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1 border text-violet-700 border-violet-300 hover:bg-violet-50 bg-white"
                                            data-testid={`org-task-snooze-${t.id}`}
                                        >
                                            <Clock className="w-3 h-3" /> Schlummern
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); openReassign(t); }}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1 border text-blue-700 border-blue-300 hover:bg-blue-50 bg-white"
                                            data-testid={`org-task-reassign-${t.id}`}
                                        >
                                            <UserPlus className="w-3 h-3" /> Neu zuweisen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); markDone(t.id); }}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1 border text-emerald-700 border-emerald-300 hover:bg-emerald-50 bg-white"
                                            data-testid={`org-task-done-${t.id}`}
                                        >
                                            <Check className="w-3 h-3" /> Erledigt
                                        </button>
                                        {snoozeFor === t.id && (
                                            <div className="w-full flex flex-wrap items-center gap-1.5 mt-1 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1.5" onClick={(e) => e.stopPropagation()} data-testid={`org-task-snooze-panel-${t.id}`}>
                                                <span className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Schlummern für:</span>
                                                {[["1 Tag", 1], ["3 Tage", 3], ["1 Woche", 7]].map(([lbl, d]) => (
                                                    <button key={d} type="button" onClick={() => snooze(t.id, { days: d })}
                                                        className="text-[10px] font-bold rounded-sm px-2 py-1 border border-violet-300 bg-white text-violet-800 hover:bg-violet-100"
                                                        data-testid={`org-task-snooze-${d}d-${t.id}`}>{lbl}</button>
                                                ))}
                                                <input type="date" value={snoozeDate} onChange={(e) => setSnoozeDate(e.target.value)}
                                                    min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                                                    className="h-6 px-1.5 rounded-sm border border-violet-300 text-[11px] bg-white"
                                                    data-testid={`org-task-snooze-date-${t.id}`} />
                                                <button type="button" disabled={!snoozeDate} onClick={() => snooze(t.id, { until: snoozeDate })}
                                                    className="text-[10px] font-bold rounded-sm px-2 py-1 bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
                                                    data-testid={`org-task-snooze-until-${t.id}`}>Bis Datum</button>
                                                <button type="button" onClick={() => { setSnoozeFor(null); setSnoozeDate(""); }}
                                                    className="p-1 text-violet-500 hover:text-violet-800"><X className="w-3.5 h-3.5" /></button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
                {items.length > 10 && (
                    <div className="flex items-center justify-between gap-2 mt-2 flex-wrap" data-testid="org-tasks-pagination">
                        <div className="flex items-center gap-1" data-testid="org-tasks-pagesize">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mr-0.5">Pro Seite</span>
                            {[10, 20, 50].map((sz) => (
                                <button key={sz} type="button" onClick={() => { setPageSize(sz); setPage(0); }}
                                    className={`h-6 px-2 rounded-sm text-[10px] font-bold border transition-colors ${pageSize === sz ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                                    data-testid={`org-tasks-pagesize-${sz}`}>{sz}</button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 font-mono-num" data-testid="org-tasks-page-info">
                                {Math.min(page * pageSize + 1, items.length)}–{Math.min((page + 1) * pageSize, items.length)} / {items.length}
                            </span>
                            <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                                className="h-6 px-2 rounded-sm text-[11px] font-bold border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                                data-testid="org-tasks-prev">‹</button>
                            <button type="button" disabled={(page + 1) * pageSize >= items.length} onClick={() => setPage((p) => p + 1)}
                                className="h-6 px-2 rounded-sm text-[11px] font-bold border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                                data-testid="org-tasks-next">›</button>
                        </div>
                    </div>
                )}
                </>
            )}
            </>)}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md" data-testid="org-task-dialog">
                    <DialogHeader>
                        <DialogTitle>Aufgabe an Filiale zuweisen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-1">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Standort</label>
                            <select
                                value={form.branch_id}
                                onChange={(e) => setForm((f) => ({ ...f, branch_id: e.target.value, assignee_id: "" }))}
                                className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm bg-white"
                                data-testid="org-task-branch"
                            >
                                <option value="">— Standort wählen —</option>
                                {empGroups.map((g) => (
                                    <option key={g.dealer_id} value={g.dealer_id}>
                                        {g.site_code} · {g.company_name}{g.is_self ? " ★" : ""} ({g.employees.length})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Mitarbeiter</label>
                            <select
                                value={form.assignee_id}
                                onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))}
                                disabled={!form.branch_id}
                                className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm bg-white disabled:bg-gray-100"
                                data-testid="org-task-assignee"
                            >
                                <option value="">{form.branch_id ? (branchEmployees.length ? "— Mitarbeiter wählen —" : "Keine Login-Mitarbeiter") : "Erst Standort wählen"}</option>
                                {branchEmployees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.name}{e.employee_function ? ` · ${e.employee_function}` : ""}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Bereich</label>
                            <select
                                value={form.area}
                                onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
                                className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm bg-white"
                                data-testid="org-task-area"
                            >
                                {TASK_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Titel</label>
                            <input
                                type="text" value={form.title}
                                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                placeholder="Was ist zu tun?"
                                className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm"
                                data-testid="org-task-title"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Beschreibung (optional)</label>
                            <textarea
                                value={form.body}
                                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                                rows={2}
                                className="mt-1 w-full px-2 py-1.5 rounded-sm border border-gray-300 text-sm"
                                data-testid="org-task-body"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            type="button" onClick={submit} disabled={saving}
                            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#111827] bg-[#FFCC00] hover:brightness-95 rounded-sm px-4 py-2 disabled:opacity-60"
                            data-testid="org-task-submit"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Zuweisen
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!reassignTask} onOpenChange={(v) => { if (!v) setReassignTask(null); }}>
                <DialogContent className="max-w-md" data-testid="org-task-reassign-dialog">
                    <DialogHeader>
                        <DialogTitle>Aufgabe neu zuweisen</DialogTitle>
                    </DialogHeader>
                    {reassignTask && (
                        <div className="space-y-3 py-1">
                            <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-2">
                                <span className="font-bold">{reassignTask.title}</span>
                                <span className="text-gray-400"> · bisher: {reassignTask.assignee_name}</span>
                            </p>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Standort</label>
                                <select
                                    value={reForm.branch_id}
                                    onChange={(e) => setReForm((f) => ({ ...f, branch_id: e.target.value, assignee_id: "" }))}
                                    className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm bg-white"
                                    data-testid="org-task-reassign-branch"
                                >
                                    <option value="">— Standort wählen —</option>
                                    {empGroups.map((g) => (
                                        <option key={g.dealer_id} value={g.dealer_id}>
                                            {g.site_code} · {g.company_name}{g.is_self ? " ★" : ""} ({g.employees.length})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Neuer Mitarbeiter</label>
                                <select
                                    value={reForm.assignee_id}
                                    onChange={(e) => setReForm((f) => ({ ...f, assignee_id: e.target.value }))}
                                    disabled={!reForm.branch_id}
                                    className="mt-1 w-full h-9 px-2 rounded-sm border border-gray-300 text-sm bg-white disabled:bg-gray-100"
                                    data-testid="org-task-reassign-assignee"
                                >
                                    <option value="">{reForm.branch_id ? (reBranchEmployees.length ? "— Mitarbeiter wählen —" : "Keine Login-Mitarbeiter") : "Erst Standort wählen"}</option>
                                    {reBranchEmployees.map((e) => (
                                        <option key={e.id} value={e.id}>{e.name}{e.employee_function ? ` · ${e.employee_function}` : ""}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Info an den neuen Bearbeiter (optional)</label>
                                <textarea
                                    value={reForm.note}
                                    onChange={(e) => setReForm((f) => ({ ...f, note: e.target.value }))}
                                    rows={2}
                                    placeholder="z. B. Bitte vorrangig erledigen — Kunde wartet"
                                    className="mt-1 w-full px-2 py-1.5 rounded-sm border border-gray-300 text-sm"
                                    data-testid="org-task-reassign-note"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <button
                            type="button" onClick={submitReassign} disabled={reBusy}
                            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#111827] bg-[#FFCC00] hover:brightness-95 rounded-sm px-4 py-2 disabled:opacity-60"
                            data-testid="org-task-reassign-submit"
                        >
                            {reBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Neu zuweisen
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
